import { css, cx } from '@linaria/core'
import { createRef, type FC, lazy, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from '#components/Dropdown'
import { IconButton } from '#components/IconButton'
import { Spinner } from '#components/Spinner'
import ReportFilledIcon from '#icons/report-filled.svg'
import ReportOutlineIcon from '#icons/report-outline.svg'
import { style } from '#styles'

const dropdownClass = css`
  display: flex;
  align-items: center;
`

const overlayStyle = css`
  pointer-events: none;
  background: none;
  // put above highlight
  z-index: 1;
  > * {
    pointer-events: auto;
  }
`

const popupStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  list-style: none;
  background-color: ${style.themeColors.backgroundPopout};
  color: ${style.themeColors.textDefault};
  outline: 0;
  box-shadow: 0px 1px 21.7px 0px rgba(0, 0, 0, 0.25);
  max-width: 320px;
`

export interface FeedbackDropdownProps {
  className?: string
  customLabels?: string[]
}

const LazyDialogContent = lazy(async () => {
  const { FeedbackDialogContent } = await import('#features/Feedback/FeedbackDialogContent.tsx')
  return { default: FeedbackDialogContent }
})

export const FeedbackDropdown: FC<FeedbackDropdownProps> = ({ className, customLabels }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('shared')
  const anchorRef = createRef<HTMLDivElement>()
  const popupRef = createRef<HTMLDivElement>()

  return (
    <Dropdown
      className={cx(dropdownClass, 'element-picker')}
      popupClassName={popupStyle}
      popupRef={popupRef}
      ref={anchorRef}
      overlayClassName={cx(overlayStyle, 'element-picker')}
      open={isOpen}
      onOpenChange={setIsOpen}
      dismissOnClickOutside={false}
      offset={7.5}
      dropdownContent={
        <Suspense fallback={<Spinner subtitle={t('app.loading-data')} />}>
          {isOpen ? (
            <LazyDialogContent
              buttonRef={anchorRef}
              popupRef={popupRef}
              customLabels={customLabels}
              onClose={() => {
                setIsOpen(false)
              }}
            />
          ) : (
            <Spinner subtitle={t('app.loading-data')} />
          )}
        </Suspense>
      }
    >
      <IconButton
        iconSize="m"
        className={cx(className, 'icon-left')}
        style={{ opacity: 1 }}
        iconUrl={isOpen ? ReportFilledIcon : ReportOutlineIcon}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        title={t('feedback.button.show-feedback', 'Give Feedback')}
      />
    </Dropdown>
  )
}
